import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ShoppingCart, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { BrandChip } from '@/components/scope-chips';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { conversationClient } from '@/lib/gupshup-client';
import { ApiError } from '@/lib/api-error';
import { draftOrderClient, type DraftStartContext, type StartDraftPrefillSkip } from '@/lib/draft-order-client';
import { ordersClient, type Order } from '@/lib/orders-client';
import { OrderDetailDialog } from '@/components/orders/order-detail-dialog';
import { SwitchPaymentMethodDialog } from '@/components/orders/switch-payment-method-dialog';
import { CancelOrderDialog } from '@/components/orders/board/cancel-order-dialog';
import { useOrderPaymentActions } from '@/components/orders/use-order-payment-actions';
import type { CartBranch } from './cart-delivery-section';
import {
  AwaitingPaymentBlock,
  RecentOrderNotice,
  StartOrderDialog,
  type AwaitingPaymentOrder,
} from './start-order-dialog';

interface CartStartPanelProps {
  conversationId: string;
  /** Aggregate sessions only: the brand this conversation belongs to. */
  brandName?: string;
  /** Branches this session may place for, already scoped to it. */
  branches: CartBranch[];
  startContext?: DraftStartContext;
  /** True while the conversation's AI is paused — i.e. the operator already holds the floor. */
  hasTakenOver: boolean;
  onClose: () => void;
  /** The draft now exists: re-read it (and the conversation, whose AI is now paused). */
  onStarted: () => void;
}

/**
 * The cart panel for a conversation that has no cart.
 *
 * Same slot, same chrome as the live panel — the operator opens "the cart" and finds either the order
 * in progress or the way to begin one, rather than a button that appears somewhere else entirely.
 *
 * The branch is chosen here, before the cart exists, because it is the one decision that cannot be
 * revised later: an order's branch is locked once pinned (the customer was quoted that branch's menu,
 * hours and delivery zone), and it is what the address prefill is priced against.
 */
export function CartStartPanel({
  conversationId,
  brandName,
  branches,
  startContext,
  hasTakenOver,
  onClose,
  onStarted,
}: CartStartPanelProps) {
  const { t } = useTranslation('conversations');

  const [dialogOpen, setDialogOpen] = useState(false);
  const [starting, setStarting] = useState(false);
  const [locationId, setLocationId] = useState<string | undefined>(startContext?.suggestedLocationId);
  /** The start was refused because an unpaid order holds the customer — kept until that order is resolved. */
  const [refusedBy, setRefusedBy] = useState<AwaitingPaymentOrder | null>(null);
  /** The unpaid order, read in full for whichever of its actions is open. */
  const [viewingOrder, setViewingOrder] = useState<Order | null>(null);
  const [switchingOrder, setSwitchingOrder] = useState<Order | null>(null);
  const [cancellingOrderId, setCancellingOrderId] = useState<string | null>(null);
  const [loadingOrder, setLoadingOrder] = useState(false);

  const recentOrder = startContext?.recentOrder;
  const awaitingPayment: AwaitingPaymentOrder | null =
    refusedBy ??
    (recentOrder?.status === 'pending_payment'
      ? { orderId: recentOrder.orderId, placedAt: recentOrder.placedAt }
      : null);

  /** The unpaid order left pending_payment (switched, cancelled, paid): the way to start is open again. */
  const handleUnpaidOrderResolved = () => {
    setViewingOrder(null);
    setSwitchingOrder(null);
    setRefusedBy(null);
    // A blocked switch left a draft on this conversation, and re-reading swaps this panel for its cart.
    onStarted();
  };

  const paymentActions = useOrderPaymentActions({
    onOrderChanged: (order) => {
      if (order.status === 'pending_payment') {
        setViewingOrder((previous) => (previous?.id === order.id ? order : previous));
        return;
      }
      handleUnpaidOrderResolved();
    },
  });

  // The suggestion arrives with the poll, which can land after the panel opened. Adopting it only while
  // nothing is selected keeps it from overwriting a branch the operator just picked.
  useEffect(() => {
    setLocationId((current) => current ?? startContext?.suggestedLocationId);
  }, [startContext?.suggestedLocationId]);

  /**
   * A session pinned to one branch has nothing to choose, and neither does a one-branch brand — the
   * server resolves both on its own. The picker is for the operator who really does have to say which
   * kitchen is cooking this.
   */
  const showBranchPicker = !startContext?.locationLocked && branches.length > 1;
  const branchName = branches.find((branch) => branch.id === locationId)?.name;

  // The poll can report the unpaid order while the dialog is open; starting is no longer an option.
  const blocked = Boolean(awaitingPayment);
  useEffect(() => {
    if (blocked && !starting) setDialogOpen(false);
  }, [blocked, starting]);

  const loadUnpaidOrder = async (orderId: string): Promise<Order | null> => {
    setLoadingOrder(true);
    try {
      const response = await ordersClient.getById(orderId);
      return response.data;
    } catch (error) {
      console.error('Failed to load the unpaid order:', error);
      toast.error(t('cart.start.blocked.loadFailed'));
      return null;
    } finally {
      setLoadingOrder(false);
    }
  };

  const handleViewUnpaidOrder = async (orderId: string) => {
    const order = await loadUnpaidOrder(orderId);
    if (order) setViewingOrder(order);
  };

  const handleSwitchUnpaidOrder = async (orderId: string) => {
    const order = await loadUnpaidOrder(orderId);
    if (order) setSwitchingOrder(order);
  };

  const handleCancelConfirmed = async (orderId: string, cancelledReason: string | undefined) => {
    setCancellingOrderId(null);
    try {
      await ordersClient.updateStatus(orderId, 'cancelled', cancelledReason);
      toast.success(t('cart.start.blocked.cancelled'));
      handleUnpaidOrderResolved();
    } catch (error) {
      console.error('Failed to cancel the unpaid order:', error);
      if (!paymentActions.reportError(orderId, error)) toast.error(t('cart.start.blocked.cancelFailed'));
    }
  };

  const handleStart = async () => {
    setStarting(true);
    try {
      // Pause the AI FIRST: a draft that exists while the agent is still driving is a cart with two
      // writers, and the agent would narrate one it did not build. A failure here stops the whole
      // thing rather than leaving that window open.
      await conversationClient.conversations.takeover(conversationId);
    } catch {
      setStarting(false);
      toast.error(t('cart.errors.takeoverFailed'));
      return;
    }

    try {
      const result = await draftOrderClient.start(conversationId, { locationId });
      setDialogOpen(false);
      // Hand-back has already been offered to the operator; this only reports what the cart opened with.
      if (result.alreadyExisted) {
        // Nothing was created — the conversation already had a live cart (the customer's own order
        // landed first) and the panel is about to show it. Saying "order started" would be a lie.
        toast.info(t('cart.start.toasts.existing'));
      } else {
        toast.success(
          result.prefill.applied.length > 0 ? t('cart.start.toasts.prefilled') : t('cart.start.toasts.empty'),
        );
        const warning = describeSkip(result.prefill.skipped);
        if (warning) toast.warning(t(warning as 'cart.start.toasts.addressOutOfZone'));
      }
      onStarted();
    } catch (error) {
      // The customer's unpaid order landed (or was only now noticed) between loading and starting.
      const refusal = awaitingPaymentRefusal(error);
      if (refusal) {
        setDialogOpen(false);
        setRefusedBy(refusal);
        onStarted();
        return;
      }
      toast.error(describeStartError(error, t));
      // A refusal usually means the world moved (the customer's own order landed first, the branch is
      // gone) — re-read so the panel stops arguing from what it loaded.
      onStarted();
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="absolute inset-0 z-20 flex h-full w-full flex-col overflow-hidden bg-card md:static md:z-auto md:w-[26rem] md:shrink-0 md:border-l md:border-border">
      <div className="flex h-14 shrink-0 items-center justify-between border-b border-border px-4">
        <div className="flex items-center gap-2">
          <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-semibold text-foreground">{t('cart.start.title')}</span>
          <BrandChip brandName={brandName} />
        </div>
        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose} aria-label={t('cart.close')}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex-1 space-y-5 overflow-y-auto p-4">
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed border-border px-4 py-8 text-center">
          <ShoppingCart className="h-7 w-7 text-muted-foreground" />
          <p className="text-sm font-medium text-foreground">{t('cart.start.empty')}</p>
          <p className="text-xs text-muted-foreground">{t('cart.start.emptyHint')}</p>
        </div>

        {awaitingPayment ? (
          <AwaitingPaymentBlock
            order={awaitingPayment}
            holdCode={
              paymentActions.notice?.orderId === awaitingPayment.orderId ? paymentActions.notice.code : undefined
            }
            busy={loadingOrder}
            onViewOrder={() => void handleViewUnpaidOrder(awaitingPayment.orderId)}
            onSwitchPayment={() => void handleSwitchUnpaidOrder(awaitingPayment.orderId)}
            onCancelOrder={() => setCancellingOrderId(awaitingPayment.orderId)}
          />
        ) : (
          recentOrder && <RecentOrderNotice order={recentOrder} />
        )}

        {showBranchPicker && (
          <div className="space-y-1.5">
            <Label htmlFor="cart-start-branch" className="text-sm font-medium">
              {t('cart.delivery.branchLabel')}
            </Label>
            <Select value={locationId ?? ''} onValueChange={setLocationId}>
              <SelectTrigger id="cart-start-branch" className="w-full" aria-label={t('cart.delivery.branchLabel')}>
                <SelectValue placeholder={t('cart.delivery.branchPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                {branches.map((branch) => (
                  <SelectItem key={branch.id} value={branch.id}>
                    {branch.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{t('cart.delivery.branchHint')}</p>
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-border p-4">
        <Button
          className="w-full"
          disabled={blocked || (showBranchPicker && !locationId)}
          title={
            blocked
              ? t('cart.start.blocked.startDisabled')
              : showBranchPicker && !locationId
                ? t('cart.start.pickBranchFirst')
                : undefined
          }
          onClick={() => setDialogOpen(true)}
        >
          <ShoppingCart className="h-3.5 w-3.5" />
          {t('cart.start.action')}
        </Button>
      </div>

      <StartOrderDialog
        open={dialogOpen}
        alreadyPaused={hasTakenOver}
        branchName={branchName}
        recentOrder={startContext?.recentOrder}
        submitting={starting}
        onCancel={() => !starting && setDialogOpen(false)}
        onConfirm={() => void handleStart()}
      />

      {/* The unpaid order's own detail, switch and cancel — the same ones the orders surfaces use. */}
      <OrderDetailDialog
        order={viewingOrder}
        onClose={() => setViewingOrder(null)}
        // Unpaid, its only status move is cancel; the panel closes as soon as it leaves pending_payment.
        onStatusChange={(orderId, status) => {
          if (status === 'cancelled') setCancellingOrderId(orderId);
        }}
        paymentActions={paymentActions}
      />
      <SwitchPaymentMethodDialog
        order={switchingOrder}
        notice={paymentActions.notice}
        onSwitch={paymentActions.switchPaymentMethod}
        onSwitched={() => setSwitchingOrder(null)}
        onDismiss={() => setSwitchingOrder(null)}
      />
      <CancelOrderDialog
        orderId={cancellingOrderId}
        onConfirm={(reason) => {
          if (cancellingOrderId) void handleCancelConfirmed(cancellingOrderId, reason);
        }}
        onDismiss={() => setCancellingOrderId(null)}
      />
    </div>
  );
}

/**
 * The unpaid order behind a `409 ORDER_AWAITING_PAYMENT` start refusal, whose payload sits under the
 * envelope's `details` (an `OrderAuthoringError`'s data); `data` is read too for a body that bypasses
 * the envelope.
 */
function awaitingPaymentRefusal(error: unknown): AwaitingPaymentOrder | null {
  if (!(error instanceof ApiError) || error.code !== 'ORDER_AWAITING_PAYMENT') return null;
  const data = (error.data.details ?? error.data.data ?? {}) as Record<string, unknown>;
  if (typeof data.orderId !== 'string') return null;
  return {
    orderId: data.orderId,
    // Decimal columns can arrive as strings.
    total: data.total != null && Number.isFinite(Number(data.total)) ? Number(data.total) : undefined,
    currency: typeof data.currency === 'string' ? data.currency : undefined,
    paymentDeadlineAt: typeof data.paymentDeadlineAt === 'string' ? data.paymentDeadlineAt : undefined,
  };
}

/**
 * The one prefill failure worth interrupting the operator about: the cart opened without something the
 * customer's history offered, and they would otherwise only notice at the blockers list. A customer with
 * no saved address is not news — the delivery section is simply empty.
 */
function describeSkip(skipped: StartDraftPrefillSkip[]): string | undefined {
  if (skipped.includes('DELIVERY_NOT_AVAILABLE')) return 'cart.start.toasts.deliveryUnavailable';
  if (skipped.includes('PICKUP_NOT_AVAILABLE')) return 'cart.start.toasts.pickupUnavailable';
  if (skipped.includes('ADDRESS_OUT_OF_ZONE')) return 'cart.start.toasts.addressOutOfZone';
  if (skipped.includes('ADDRESS_NOT_REUSABLE')) return 'cart.start.toasts.addressNotReusable';
  return undefined;
}

function describeStartError(error: unknown, t: ReturnType<typeof useTranslation<'conversations'>>['t']): string {
  if (!(error instanceof ApiError)) return t('cart.start.errors.generic');
  const specific = t(`cart.errors.${error.code}` as const, { defaultValue: '' });
  return specific || t('cart.start.errors.generic');
}
